import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import type { PunditUsersObject } from "@circle-react/hooks/usePunditAPI";
import { punditUserKey } from "@circle-react/lib/queries/punditUser";
import type { CurrentPlan } from "@circle-react/types";
import { useQueryClient } from "react-query";

import type { UpcomingInvoiceResponse } from "@/react/components/SettingsApp/Plan/hooks/useUpcomingInvoiceByProductApi";

import { useAccountSubscriptionApi } from "./useAccountSubscriptionApi";

export const BILLING_REFRESHING_QUERY_KEY = "billing-is-refreshing";
const UPCOMING_INVOICE_QUERY_KEY = "community-upcoming-invoice-by-product";
const CURRENT_PLAN_QUERY_KEY = "community-billing-details";
const CURRENT_PLAN_API_QUERY_KEY = "community-current-plan";
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 8;

const restoreEmailHubSubscription = (plan: CurrentPlan | undefined) => {
  if (!plan?.email_hub_billing) return plan;
  return {
    ...plan,
    email_hub_billing: {
      ...plan.email_hub_billing,
      cancel_at_period_end: false,
      billing_status: "active" as const,
    },
  };
};

const restoreSubscriptionInCache = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.setQueryData<PunditUsersObject | undefined>(
    punditUserKey.root(),
    user => {
      if (!user?.current_community?.subscription) return user;
      return {
        ...user,
        current_community: {
          ...user.current_community,
          subscription: {
            ...user.current_community.subscription,
            is_cancelled: false,
            on_grace_period: false,
            ends_at: null,
          },
        },
      };
    },
  );
};

const restoreEmailHubSubscriptionInCache = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.setQueryData<CurrentPlan | undefined>(
    [CURRENT_PLAN_QUERY_KEY],
    restoreEmailHubSubscription,
  );
  queryClient.setQueryData<CurrentPlan | undefined>(
    [CURRENT_PLAN_API_QUERY_KEY],
    restoreEmailHubSubscription,
  );
};

const fetchBillingData = (queryClient: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    queryClient.refetchQueries({ queryKey: [UPCOMING_INVOICE_QUERY_KEY] }),
    queryClient.refetchQueries({ queryKey: [CURRENT_PLAN_QUERY_KEY] }),
    queryClient.refetchQueries({ queryKey: [CURRENT_PLAN_API_QUERY_KEY] }),
  ]);

const getBillingDataFromCache = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  const invoiceData = queryClient.getQueryData<UpcomingInvoiceResponse>([
    UPCOMING_INVOICE_QUERY_KEY,
  ]);
  const planData = queryClient.getQueryData<CurrentPlan>([
    CURRENT_PLAN_QUERY_KEY,
  ]);
  return { invoiceData, planData };
};

const isBillingDataReady = (
  invoiceData: UpcomingInvoiceResponse | undefined,
  planData: CurrentPlan | undefined,
) => Boolean(invoiceData?.headline) && planData?.cancelling_metadata === null;

const refreshBillingData = (queryClient: ReturnType<typeof useQueryClient>) => {
  let attempts = 0;

  const poll = async () => {
    attempts += 1;
    await fetchBillingData(queryClient);
    const { invoiceData, planData } = getBillingDataFromCache(queryClient);
    if (
      isBillingDataReady(invoiceData, planData) ||
      attempts >= POLL_MAX_ATTEMPTS
    ) {
      queryClient.setQueryData([BILLING_REFRESHING_QUERY_KEY], false);
      return;
    }
    setTimeout(() => void poll(), POLL_INTERVAL_MS);
  };

  void poll();
};

export const useResumePlan = () => {
  const { currentCommunity } = usePunditUserContext();
  const { updateSubscription, isUpdatingSubscription } =
    useAccountSubscriptionApi({ shouldRedirect: false });
  const queryClient = useQueryClient();

  const planId = currentCommunity?.current_plan?.id;

  const resumePlan = () => {
    if (!planId) return;
    updateSubscription(planId, {
      onSuccess: () => {
        queryClient.setQueryData([BILLING_REFRESHING_QUERY_KEY], true);
        restoreSubscriptionInCache(queryClient);
        restoreEmailHubSubscriptionInCache(queryClient);
        refreshBillingData(queryClient);
      },
    });
  };

  return { resumePlan, isLoading: isUpdatingSubscription };
};
